/**
 * Base HTTP client with authentication support
 */

import type { Simulation, SimulationArtifact, Organism, GenesisConfig, OrganismSnapshot } from '../types/simulation';
import type { AuthSession, User } from '../types/auth';

const API_BASE_URL = window.location.origin;
const SESSION_KEY = 'auth_session';

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

/**
 * Get stored session from localStorage
 */
function getSession(): AuthSession | null {
  const sessionStr = localStorage.getItem(SESSION_KEY);
  if (!sessionStr) return null;

  try {
    const session = JSON.parse(sessionStr);
    return session;
  } catch {
    return null;
  }
}

/**
 * Get stored JWT token from localStorage
 */
export function getAuthToken(): string | null {
  const session = getSession();
  if (!session) return null;

  // Check if token is expired
  if (Date.now() >= session.expiresAt) {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
  return session.token;
}

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken(): Promise<string> {
  // If already refreshing, wait for that request
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const session = getSession();
      if (!session?.refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: session.refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      // Update session with new tokens
      const newSession: AuthSession = {
        token: data.token,
        refreshToken: data.refresh_token,
        expiresAt: Date.now() + (720 * 60 * 60 * 1000), // Fallback expiration
      };

      // Try to get actual expiration from JWT
      try {
        const payload = JSON.parse(atob(data.token.split('.')[1]));
        if (payload.exp) {
          newSession.expiresAt = payload.exp * 1000;
        }
      } catch {
        // Use fallback expiration
      }

      localStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
      return data.token;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Make authenticated API request with automatic token refresh on 401
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Skip refresh for auth endpoints to avoid loops
  const isAuthEndpoint = endpoint.includes('/api/auth/');

  const token = getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add auth header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Merge with provided headers
  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  let response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // If 401 and not an auth endpoint, try to refresh token
  if (response.status === 401 && !isAuthEndpoint) {
    try {
      const newToken = await refreshAccessToken();

      // Retry original request with new token
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });
    } catch (refreshError) {
      // Refresh failed, clear session and redirect to login
      localStorage.removeItem(SESSION_KEY);
      window.location.href = '/';
      throw new Error('Session expired. Please login again.');
    }
  }

  // Handle error responses
  if (!response.ok) {
    const error = await response.json().catch(() => ({
      detail: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(error.detail || 'Request failed');
  }

  return response.json();
}

/**
 * Get list of available simulations
 */
export async function getSimulations(): Promise<Simulation[]> {
  return apiRequest<Simulation[]>('/api/simulations');
}

/**
 * Get simulation details
 */
export async function getSimulation(simulationId: string): Promise<Simulation> {
  return apiRequest<Simulation>(`/api/simulations/${simulationId}`);
}

/**
 * Delete a simulation
 */
export async function getSimulationArtifact(simulationId: string): Promise<SimulationArtifact> {
  return apiRequest<SimulationArtifact>(`/api/simulations/${simulationId}/genesis/artifact`);
}

export async function getSimulationConfig(simulationId: string): Promise<GenesisConfig> {
  return apiRequest<GenesisConfig>(`/api/simulations/${simulationId}/genesis/config`);
}

export async function deleteSimulation(simulationId: string): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/api/simulations/${simulationId}`, {
    method: 'DELETE',
  });
}

/**
 * Genesis: list active organisms in a simulation
 */
export async function listOrganisms(simulationId: string, alive?: boolean): Promise<Organism[]> {
  const params = alive != null ? `?alive=${alive}` : '';
  return apiRequest<Organism[]>(`/api/simulations/${simulationId}/genesis/organisms${params}`);
}

/**
 * Genesis: spawn an organism from a genome
 */
export async function spawnOrganism(
  simulationId: string,
  data: { genome_id: string; position?: [number, number, number] | null }
): Promise<Organism> {
  return apiRequest<Organism>(`/api/simulations/${simulationId}/genesis/organism`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Genesis: kill an organism by entity id
 */
export async function killOrganism(
  simulationId: string,
  entityId: string
): Promise<{ entity_id: string; status: string }> {
  return apiRequest<{ entity_id: string; status: string }>(
    `/api/simulations/${simulationId}/genesis/organism/${entityId}`,
    { method: 'DELETE' }
  );
}

/**
 * Genesis: list available genome ids
 */
export async function listGenomes(simulationId: string, dev = false): Promise<{ genome_ids: string[] }> {
  const url = `/api/simulations/${simulationId}/genesis/genomes${dev ? '?dev=true' : ''}`;
  return apiRequest<{ genome_ids: string[] }>(url);
}

/**
 * Genesis: take a snapshot of a live organism's genome and substrate
 */
export async function snapshotOrganism(
  simulationId: string,
  entityId: string,
  name?: string
): Promise<OrganismSnapshot> {
  return apiRequest(`/api/simulations/${simulationId}/genesis/organism/${entityId}/snapshot`, {
    method: 'POST',
    body: JSON.stringify(name ? { name } : {}),
  });
}

/**
 * Genesis: list all snapshots across all organisms
 */
export async function listSnapshots(simulationId: string): Promise<OrganismSnapshot[]> {
  return apiRequest(`/api/simulations/${simulationId}/genesis/snapshots`);
}

/**
 * Genesis: list snapshots for a specific organism
 */
export async function listOrganismSnapshots(simulationId: string, entityId: string): Promise<OrganismSnapshot[]> {
  return apiRequest(`/api/simulations/${simulationId}/genesis/organism/${entityId}/snapshots`);
}

/**
 * Genesis: delete a snapshot and its stored substrate file
 */
export async function deleteSnapshot(
  simulationId: string,
  snapshotId: string
): Promise<{ status: string }> {
  return apiRequest(`/api/simulations/${simulationId}/genesis/snapshot/${snapshotId}`, {
    method: 'DELETE',
  });
}

/**
 * Genesis: spawn a new organism from a snapshot
 */
export async function spawnFromSnapshot(
  simulationId: string,
  snapshotId: string,
  mutate = false
): Promise<{ snapshot_id: string; genome_id: string; substrate_inherited: boolean; mutated: boolean; status: string }> {
  return apiRequest(`/api/simulations/${simulationId}/genesis/snapshot/${snapshotId}/spawn`, {
    method: 'POST',
    body: JSON.stringify({ mutate }),
  });
}

/**
 * Admin: Get all users
 */
export async function getAllUsers(): Promise<User[]> {
  return apiRequest<User[]>('/api/admin/users');
}

/**
 * Admin: Get user by ID
 */
export async function getUserById(userId: number): Promise<User> {
  return apiRequest<User>(`/api/admin/users/${userId}`);
}

/**
 * Admin: Update user
 */
export async function updateUser(
  userId: number,
  data: {
    username?: string;
    email?: string;
    roles?: string[];  // Array of role names
    avatar_url?: string;
  }
): Promise<User> {
  return apiRequest<User>(`/api/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Admin: Delete user
 */
export async function deleteUser(userId: number): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/api/admin/users/${userId}`, {
    method: 'DELETE',
  });
}

/**
 * Admin: Get all simulations (including private)
 */
export async function getAllSimulationsAdmin(): Promise<Simulation[]> {
  return apiRequest<Simulation[]>('/api/admin/simulations');
}

/**
 * Admin: Create AI user
 */
export async function createAIUser(data: {
  username: string;
  display_name?: string;
}): Promise<User & { ws_token: string }> {
  return apiRequest<User & { ws_token: string }>('/api/admin/users/ai', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface APIKey {
  id: number;
  key: string;
  name: string | null;
  user_id: number;
  created_at: string;
  last_used_at: string | null;
  revoked: boolean;
}

/**
 * Admin: Get API keys for a user
 */
export async function getUserAPIKeys(userId: number): Promise<APIKey[]> {
  return apiRequest(`/api/admin/users/${userId}/api-keys`);
}

/**
 * Get your own API keys
 */
export async function getMyAPIKeys(): Promise<APIKey[]> {
  return apiRequest('/api/users/me/api-keys');
}

/**
 * Create a new API key for yourself
 */
export async function createMyAPIKey(name?: string): Promise<APIKey> {
  return apiRequest('/api/users/me/api-keys', {
    method: 'POST',
    body: JSON.stringify({ name: name || null }),
  });
}

/**
 * Revoke one of your API keys
 */
export async function revokeMyAPIKey(keyId: number): Promise<void> {
  return apiRequest(`/api/users/me/api-keys/${keyId}`, {
    method: 'DELETE',
  });
}
